import React, { useEffect, useMemo, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'

import {
  calculateCriticalBudgetRatioByMissionId,
  CalculateCriticalCompletedProjectsRatio,
  CalculateOverdueProjectRatio,
  CalculateProjectTypeBudgetRatios,
  CountAppRelatedIncidentsRatio,
  countCriticalApplicationIncidentsRatio,
  countCriticalDMZIncidentsRatio,
  CountCriticalNetworkIncidentsRatio,
  countCriticalPcIncidentsRatio,
  CountIncidentsByTypeForMission,
  CountIncidentsCriticalOrHighRatioByMission,
  CountIncidentsWithSlaUnder24Ratio,
  CriticalServerIncidentsRatio,
  getMissionbyId,
  getProjectCountsByPriorityAndStatus,
  getProjectCountsByTypeAndStatus,
  getProjectPriorityCriticalHighRatio,
  getProjectPriorityCriticalRatio,
  getProjectPriorityHighRatio,
  getProjectsBudgetTable,
  getprojetCountsByPriority,
  GetVulnerabilityDistributionByMissionId,
} from '../../redux/missions/missionActions'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { analysisColorPalette } from '../../utils/colors'
import PieChart, { hasChartData } from '../../components/common/PieChart'
import MatrixComponent from '../../components/common/MatrixTable'
import BarChart from '../../components/common/BarChart'
import { Mission, Ratios } from '../../types/missionTypes'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import {
  AnalysisChartCard,
  AnalysisPageLoader,
  AnalysisSection,
  barFill,
  pctTone,
} from '../../components/common/analysisLayout'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import { formatCurrency, locale, formatDecimal } from '../../utils/formatNumbers'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatTitle } from '../../utils/formatTitle'
import { writeEntityNav } from '../../utils/entityNav'
import '../../components/common/maturity-analysis.css'
import '../../components/common/ListPageTable.css'

const COLORS = analysisColorPalette

export const options = {
  plugins: {
    legend: {
      display: false,
    },
  },
}
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

const MissionIndicatorPage = () => {
  const dispatch: AppDispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [Mission, setMission] = useState<Mission>({
    _id: '',
    MissionId: '',
    MissionName: '',
    Description: '',
    Budget: 0,
  })
  const { MissionId } = useParams<{ MissionId: string }>()
  const MissionsList = useSelector((state: RootState) => state.mission.mission)

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!MissionId) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await dispatch(
          getProjectPriorityCriticalHighRatio({ idMission: MissionId })
        )
        dispatch(getMissionbyId(MissionId))

        await dispatch(getProjectPriorityHighRatio({ idMission: MissionId }))
        await dispatch(getprojetCountsByPriority({ idMission: MissionId }))
        await dispatch(getProjectsBudgetTable({ idMission: MissionId }))
        await dispatch(
          CalculateCriticalCompletedProjectsRatio({ idMission: MissionId })
        )
        await dispatch(
          calculateCriticalBudgetRatioByMissionId({ idMission: MissionId })
        )
        await dispatch(
          CalculateProjectTypeBudgetRatios({ idMission: MissionId })
        )

        await dispatch(CalculateOverdueProjectRatio({ idMission: MissionId }))

        await dispatch(
          getProjectPriorityCriticalRatio({ idMission: MissionId })
        )
        await dispatch(
          getProjectCountsByPriorityAndStatus({ idMission: MissionId })
        )
        await dispatch(
          getProjectCountsByTypeAndStatus({ idMission: MissionId })
        )
        await dispatch(
          CountIncidentsCriticalOrHighRatioByMission({ idMission: MissionId })
        )

        await dispatch(CountIncidentsByTypeForMission({ idMission: MissionId }))

        await dispatch(
          CountIncidentsWithSlaUnder24Ratio({ idMission: MissionId })
        )
        await dispatch(
          CountCriticalNetworkIncidentsRatio({ idMission: MissionId })
        )
        await dispatch(CountAppRelatedIncidentsRatio({ idMission: MissionId }))

        await dispatch(CriticalServerIncidentsRatio({ idMission: MissionId }))

        await dispatch(
          countCriticalApplicationIncidentsRatio({ idMission: MissionId })
        )
        await dispatch(countCriticalDMZIncidentsRatio({ idMission: MissionId }))
        await dispatch(countCriticalPcIncidentsRatio({ idMission: MissionId }))
        await dispatch(
          GetVulnerabilityDistributionByMissionId({ idMission: MissionId })
        )
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [MissionId, dispatch])

  const ProjectPriorityCriticalHighRatio = useSelector(
    (state: RootState) => state.mission.projectPriorityCriticalRatio
  )

  const criticalCompletedProjectsRatio = useSelector(
    (state: RootState) => state.mission.calculateCriticalCompletedProjectsRatio
  )

  const OverdueProjectRatio = useSelector(
    (state: RootState) => state.mission.calculateOverdueProjectRatio
  )
  const ProjectTypeBudgetRatios = useSelector(
    (state: RootState) => state.mission.calculateProjectTypeBudgetRatios
  )

  const calculProjectCritiqueBudget = useSelector(
    (state: RootState) => state.mission.calculateCriticalBudgetRatio
  )

  const projectPriorityHighRatio = useSelector(
    (state: RootState) => state.mission.projectPriorityHighRatio
  )

  const ProjectPriorityCriticalRatio = useSelector(
    (state: RootState) => state.mission.projectPriorityCriticalRatio?.ratio
  )

  const ProjectNormalPriority =
    ProjectPriorityCriticalRatio !== undefined
      ? 1 - Number(ProjectPriorityCriticalRatio)
      : 0

  const ProjectCountsByTypeAndStatus = useSelector(
    (state: RootState) => state.mission.projectCountsByTypeAndStatus?.counts
  )
  const projectCountsByPriorityAndStatus = useSelector(
    (state: RootState) => state.mission.ProjectCountsByPriorityAndStatus
  )

  const remainingRatio =
    1 -
    ((Number(projectPriorityHighRatio) || 0) +
      (Number(ProjectPriorityCriticalRatio) || 0))

  const projetCountsByPriorityCritical = useSelector(
    (state: RootState) => state.mission.projetCountsByPriority?.counts[1]
  )
  const projetCountByTypeAndStatus = useSelector(
    (state: RootState) => state.mission.projectCountsByTypeAndStatus?.counts
  )

  const projetCountsByPriorityhigh = useSelector(
    (state: RootState) => state.mission.projetCountsByPriority?.counts[2]
  )

  const projetCountsByPriorityMedium = useSelector(
    (state: RootState) => state.mission.projetCountsByPriority?.counts[3]
  )
  const projetCountsByPriorityLow = useSelector(
    (state: RootState) => state.mission.projetCountsByPriority?.counts[4]
  )

  const projetBudgetTable = useSelector(
    (state: RootState) => state.mission.ProjectsBudgetTable
  )

  const projectRateData = {
    labels: [
      t('charts.ProjectPriority.priorityRate.criticalOrHigh'),
      t('charts.ProjectPriority.priorityRate.normal'),
    ],
    datasets: [
      {
        data: [ProjectPriorityCriticalRatio ?? 0, ProjectNormalPriority ?? 0],
        backgroundColor: [COLORS[0], COLORS[2]],
        hoverBackgroundColor: [COLORS[0], COLORS[2]],
      },
    ],
  }
  const taskTypesData = {
    labels: [
      t('charts.ProjectCountsByTypeAndStatus.labels.Gouvernance du SI'),
      t(
        'charts.ProjectCountsByTypeAndStatus.labels.Gestion de la sécurité du SI'
      ),
      t('charts.ProjectCountsByTypeAndStatus.labels.Physique'),
      t('charts.ProjectCountsByTypeAndStatus.labels.Organisationnel/Processus'),
      t('charts.ProjectCountsByTypeAndStatus.labels.Opérationnel'),
      t('charts.ProjectCountsByTypeAndStatus.labels.IT/DSI'),
      t('charts.ProjectCountsByTypeAndStatus.labels.IT/Métier'),
      t('charts.ProjectCountsByTypeAndStatus.labels.Autres'),
    ],
    datasets: [
      {
        label: t('charts.ProjectCountsByTypeAndStatus.datasets.todo'),
        data: [
          ProjectCountsByTypeAndStatus?.['Gouvernance du SI']?.['A faire'] ?? 0,
          ProjectCountsByTypeAndStatus?.['Gestion de la sécurité du SI']?.[
            'A faire'
          ] ?? 0,
          ProjectCountsByTypeAndStatus?.Physique?.['A faire'] ?? 0,
          ProjectCountsByTypeAndStatus?.['Organisationnel/Processus']?.[
            'A faire'
          ] ?? 0,
          ProjectCountsByTypeAndStatus?.Opérationnel?.['A faire'] ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/DSI']?.['A faire'] ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/Métier']?.['A faire'] ?? 0,
          ProjectCountsByTypeAndStatus?.Autres?.['A faire'] ?? 0,
        ],
        backgroundColor: COLORS,
        hoverBackgroundColor: COLORS,
        borderColor: COLORS,
        borderWidth: 2,
      },
      {
        label: t('charts.ProjectCountsByTypeAndStatus.datasets.pending'),
        data: [
          ProjectCountsByTypeAndStatus?.['Gouvernance du SI']?.['En cours'] ??
            0,
          ProjectCountsByTypeAndStatus?.['Gestion de la sécurité du SI']?.[
            'En cours'
          ] ?? 0,
          ProjectCountsByTypeAndStatus?.Physique?.['En cours'] ?? 0,
          ProjectCountsByTypeAndStatus?.['Organisationnel/Processus']?.[
            'En cours'
          ] ?? 0,
          ProjectCountsByTypeAndStatus?.Opérationnel?.['En cours'] ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/DSI']?.['En cours'] ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/Métier']?.['En cours'] ?? 0,
          ProjectCountsByTypeAndStatus?.Autres?.['En cours'] ?? 0,
        ],
        backgroundColor: COLORS,
        hoverBackgroundColor: COLORS,
        borderColor: COLORS,
        borderWidth: 2,
      },
      {
        label: t('charts.ProjectCountsByTypeAndStatus.datasets.done'),
        data: [
          ProjectCountsByTypeAndStatus?.['Gouvernance du SI']?.Terminé ?? 0,
          ProjectCountsByTypeAndStatus?.['Gestion de la sécurité du SI']
            ?.Terminé ?? 0,
          ProjectCountsByTypeAndStatus?.Physique?.Terminé ?? 0,
          ProjectCountsByTypeAndStatus?.['Organisationnel/Processus']
            ?.Terminé ?? 0,
          ProjectCountsByTypeAndStatus?.Opérationnel?.Terminé ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/DSI']?.Terminé ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/Métier']?.Terminé ?? 0,
          ProjectCountsByTypeAndStatus?.Autres?.Terminé ?? 0,
        ],
        backgroundColor: COLORS,
        hoverBackgroundColor: COLORS,
        borderColor: COLORS,
        borderWidth: 2,
      },
      {
        label: t('charts.ProjectCountsByTypeAndStatus.datasets.suspended'),
        data: [
          ProjectCountsByTypeAndStatus?.['Gouvernance du SI']?.Suspendu ?? 0,
          ProjectCountsByTypeAndStatus?.['Gestion de la sécurité du SI']
            ?.Suspendu ?? 0,
          ProjectCountsByTypeAndStatus?.Physique?.Suspendu ?? 0,
          ProjectCountsByTypeAndStatus?.['Organisationnel/Processus']
            ?.Suspendu ?? 0,
          ProjectCountsByTypeAndStatus?.Opérationnel?.Suspendu ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/DSI']?.Suspendu ?? 0,
          ProjectCountsByTypeAndStatus?.['IT/Métier']?.Suspendu ?? 0,
          ProjectCountsByTypeAndStatus?.Autres?.Suspendu ?? 0,
        ],
        backgroundColor: COLORS,
        hoverBackgroundColor: COLORS,
        borderColor: COLORS,
        borderWidth: 2,
      },
    ],
  }

  const ProjectPriorityData = {
    labels: [
      t('charts.ProjectStatusPriority.labels.critical'),
      t('charts.ProjectStatusPriority.labels.high'),
      t('charts.ProjectStatusPriority.labels.medium'),
      t('charts.ProjectStatusPriority.labels.low'),
    ],
    datasets: [
      {
        label: t('charts.ProjectStatusPriority.datasets.label'),
        data: [
          projetCountsByPriorityCritical ?? 0,
          projetCountsByPriorityhigh ?? 0,
          projetCountsByPriorityMedium ?? 0,
          projetCountsByPriorityLow ?? 0,
        ], // Default to 0 if undefined
        backgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ], // Array of colors
        hoverBackgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ], // Array of colors
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ], // Array of colors for borders
        borderWidth: 2, // Set border width
      },
    ],
  }

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Task Status by Type',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }

  const criticalityData = {
    labels: [
      t('charts.priorityStatusDistribution.labels.low'),
      t('charts.priorityStatusDistribution.labels.medium'),
      t('charts.priorityStatusDistribution.labels.high'),
      t('charts.priorityStatusDistribution.labels.critical'),
    ],
    datasets: [
      {
        label: t('charts.priorityStatusDistribution.datasets.todo'),
        data: [
          projectCountsByPriorityAndStatus?.counts.LOW['A faire'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.MEDIUM['A faire'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.HIGH['A faire'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.CRITICAL['A faire'] ?? 0,
        ],
        backgroundColor: [
          COLORS[0], // Low - same color for all Low statuses
          COLORS[1], // Medium - same color for all Medium statuses
          COLORS[2], // High - same color for all High statuses
          COLORS[3], // Critical - same color for all Critical statuses
        ],
        hoverBackgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderWidth: 2,
      },
      {
        label: t('charts.priorityStatusDistribution.datasets.pending'),
        data: [
          projectCountsByPriorityAndStatus?.counts.LOW['En cours'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.MEDIUM['En cours'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.HIGH['En cours'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.CRITICAL['En cours'] ?? 0,
        ],
        backgroundColor: [
          COLORS[0], // Low
          COLORS[1], // Medium
          COLORS[2], // High
          COLORS[3], // Critical
        ],
        hoverBackgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderWidth: 2,
      },
      {
        label: t('charts.priorityStatusDistribution.datasets.suspended'),
        data: [
          projectCountsByPriorityAndStatus?.counts.LOW.Suspendu ?? 0,
          projectCountsByPriorityAndStatus?.counts.MEDIUM.Suspendu ?? 0,
          projectCountsByPriorityAndStatus?.counts.HIGH.Suspendu ?? 0,
          projectCountsByPriorityAndStatus?.counts.CRITICAL.Suspendu ?? 0,
        ],
        backgroundColor: [
          COLORS[0], // Low
          COLORS[1], // Medium
          COLORS[2], // High
          COLORS[3], // Critical
        ],
        hoverBackgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderWidth: 2,
      },
      {
        label: t('charts.priorityStatusDistribution.datasets.done'),
        data: [
          projectCountsByPriorityAndStatus?.counts.LOW.Terminé ?? 0,
          projectCountsByPriorityAndStatus?.counts.MEDIUM.Terminé ?? 0,
          projectCountsByPriorityAndStatus?.counts.HIGH.Terminé ?? 0,
          projectCountsByPriorityAndStatus?.counts.CRITICAL.Terminé ?? 0,
        ],
        backgroundColor: [
          COLORS[0], // Low
          COLORS[1], // Medium
          COLORS[2], // High
          COLORS[3], // Critical
        ],
        hoverBackgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderWidth: 2,
      },
      {
        label: t('charts.priorityStatusDistribution.datasets.waiting'),
        data: [
          projectCountsByPriorityAndStatus?.counts.LOW['En attente'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.MEDIUM['En attente'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.HIGH['En attente'] ?? 0,
          projectCountsByPriorityAndStatus?.counts.CRITICAL['En attente'] ?? 0,
        ],
        backgroundColor: [
          COLORS[0], // Low
          COLORS[1], // Medium
          COLORS[2], // High
          COLORS[3], // Critical
        ],
        hoverBackgroundColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderColor: [
          COLORS[0],
          COLORS[1],
          COLORS[2],
          COLORS[3],
        ],
        borderWidth: 2,
      },
    ],
  }
  const delayedProjectsData = {
    labels: [
      t('charts.PercentageBudgetsCritical.critical'),
      t('charts.PercentageBudgetsCritical.remaining'),
    ],
    datasets: [
      {
        data: [
          calculProjectCritiqueBudget?.ratio ?? 0,
          calculProjectCritiqueBudget?.ratio !== undefined
            ? 1 - Number(calculProjectCritiqueBudget.ratio)
            : 0,
        ],
        backgroundColor: [COLORS[0], COLORS[2]], // Red for delayed, Blue for on-time
        hoverBackgroundColor: [COLORS[0], COLORS[2]],
      },
    ],
  }

  const criticalCompletedData = {
    labels: [
      t('charts.CriticalCompletion.labels.completed'),
      t('charts.CriticalCompletion.labels.remaining'),
    ],
    datasets: [
      {
        data: [
          criticalCompletedProjectsRatio?.ratio ?? 0,
          criticalCompletedProjectsRatio?.ratio !== undefined
            ? 1 - Number(criticalCompletedProjectsRatio.ratio)
            : 0,
        ],
        backgroundColor: [COLORS[0], COLORS[2]],
        hoverBackgroundColor: [COLORS[0], COLORS[2]],
      },
    ],
  }

  const OverdueProjectData = {
    labels: ['Overdue Project Ratio', 'Reste'],
    datasets: [
      {
        data: [
          OverdueProjectRatio?.ratio ?? 0,
          OverdueProjectRatio?.ratio !== undefined
            ? 1 - Number(OverdueProjectRatio.ratio)
            : 0,
        ],
        backgroundColor: [COLORS[0], COLORS[2]],
        hoverBackgroundColor: [COLORS[0], COLORS[2]],
      },
    ],
  }
  const ratios = (ProjectTypeBudgetRatios?.ratios || {}) as Ratios
  const total = Object.values(ratios).reduce(
    (sum: number, value: number) => sum + value,
    0
  )
  const percentages = {
    'Gestion de la sécurité du SI':
      (ratios['Gestion de la sécurité du SI'] / total) * 100,
    'Gouvernance du SI': (ratios['Gouvernance du SI'] / total) * 100,
    Opérationnel: (ratios['Opérationnel'] / total) * 100,
  }

  const BudgetTypeProjectsData = {
    labels: [
      t('charts.BudgetSecurityGovernance.labels.securityManagement'),
      t('charts.BudgetSecurityGovernance.labels.governance'),
      t('charts.BudgetSecurityGovernance.labels.operational'),
    ],
    datasets: [
      {
        data: [
          percentages['Gestion de la sécurité du SI'],
          percentages['Gouvernance du SI'],
          percentages['Opérationnel'],
        ],
        backgroundColor: [COLORS[0], COLORS[2], COLORS[1]],
        hoverBackgroundColor: [
          COLORS[0],
          COLORS[2],
          COLORS[1],
        ],
      },
    ],
  }

  const currency = useSelector(selectSpecifications)[0]?.Currency

  const projects =
    projetBudgetTable?.table.map((item) => ({
      name: item.name,
      budget: `${formatCurrency(item.budget ?? 0, currency, {
        locale,
        useIntl: true,
      })}`,
    })) || []
  const headers = [
    t('analysis.missionAnalysis.headers.projectName'),
    t('analysis.missionAnalysis.headers.budget'),
  ]

  const data = projects.map((project) => ({
    [headers[0]]: project.name,
    [headers[1]]: project.budget,
  }))

  useEffect(() => {
    if (MissionsList !== null) {
      setMission({
        _id: MissionsList._id,
        MissionId: MissionsList.MissionId,
        MissionName: MissionsList.MissionName,
        Description: MissionsList.Description,
        Budget: MissionsList.Budget,
      })
      if (MissionsList._id) {
        writeEntityNav({
          missionId: MissionsList._id,
          missionLabel: formatTitle(MissionsList.MissionName || ''),
        })
      }
    }
  }, [MissionsList])
  const countIncidentsCriticalOrHighRatio = useSelector(
    (state: RootState) =>
      state.mission.countIncidentsCriticalOrHighRatioByMission?.ratio
  )

  const countIncidentsByType = useSelector(
    (state: RootState) => state.mission.countIncidentsByTypeForMission
  )

  const CountIncidentsWithSlaUnder24 = useSelector(
    (state: RootState) => state.mission.countIncidentsWithSlaUnder24Ratio?.ratio
  )

  const CriticalNetworkIncidentsRatio = useSelector(
    (state: RootState) =>
      state.mission.countCriticalNetworkIncidentsRatio?.ratio
  )

  const CriticalServerIncidents = useSelector(
    (state: RootState) => state.mission.countCriticalServerIncidentsRatio?.ratio
  )

  const CriticalApplicationIncident = useSelector(
    (state: RootState) => state.mission.countCriticalApplicationRatio?.ratio
  )

  const CriticalPCIncident = useSelector(
    (state: RootState) => state.mission.CriticalPcIncidentsRatio?.ratio
  )

  const CriticalDMZIncident = useSelector(
    (state: RootState) => state.mission.CriticalDMZIncidentsRatio?.ratio
  )
  const { vulnerabilityDistribution } = useSelector(
    (state: RootState) => state.mission
  )

  const vulnerabilityArray = Object.values(
    vulnerabilityDistribution || {}
  ).filter((item) => typeof item === 'object' && 'vulnerability' in item)

  const VulnerabilityDistributionData: any = {
    labels: vulnerabilityArray.map((item: any) => item.vulnerability),
    datasets: [
      {
        label: t('charts.vulnerabilityDistributionData.label'),
        data: vulnerabilityArray.map((item: any) => item.count),
        backgroundColor: vulnerabilityArray.map(
          (_, i) => COLORS[i % COLORS.length]
        ),
        borderColor: vulnerabilityArray.map(
          (_, i) => COLORS[i % COLORS.length]
        ),
        borderWidth: 1,
      },
    ],
  }

  const incidentsByTypeData: any = {
    labels: [
      t('charts.incidents.types.intrusion'),
      t('charts.incidents.types.denialOfService'),
      t('charts.incidents.types.compromise'),
      t('charts.incidents.types.dataDisclosure'),
      t('charts.incidents.types.remoteCodeExecution'),
      t('charts.incidents.types.corruption'),
    ],
    datasets: [
      {
        data: [
          countIncidentsByType?.Intrusion ?? 0,
          countIncidentsByType?.['Déni de service'] ?? 0,
          countIncidentsByType?.Compromission ?? 0,
          countIncidentsByType?.['Divulgation des données'] ?? 0,
          countIncidentsByType?.['Exécution de code à distance'] ?? 0,
          countIncidentsByType?.Corruption ?? 0,
        ],
        backgroundColor: COLORS.slice(0, 6),
        hoverBackgroundColor: COLORS.slice(0, 6),
        hoverOffset: 4,
      },
    ],
  }

  const incidentsByPerimeterData = {
    labels: [
      t('charts.perimeters.network'),
      t('charts.perimeters.servers'),
      t('charts.perimeters.internalAppsCloud'),
      t('charts.perimeters.dmz'),
      t('charts.perimeters.pc'),
    ],
    datasets: [
      {
        data: [
          CriticalNetworkIncidentsRatio ?? 0,
          CriticalServerIncidents ?? 0,
          CriticalApplicationIncident ?? 0,
          CriticalDMZIncident ?? 0,
          CriticalPCIncident ?? 0,
        ],
        backgroundColor: COLORS.slice(0, 5),
        hoverBackgroundColor: COLORS.slice(0, 5),
        hoverOffset: 4,
      },
    ],
  }
  const pdf = useAnalysisPdfExport()

  const criticalIncidentsPct = Math.min(
    100,
    Math.max(0, Number(countIncidentsCriticalOrHighRatio) * 100 || 0)
  )
  const slaRaw = Number(CountIncidentsWithSlaUnder24) || 0
  const slaPct = Math.min(
    100,
    Math.max(0, slaRaw <= 1 ? slaRaw * 100 : slaRaw)
  )
  const criticalTone = pctTone(criticalIncidentsPct)
  const slaTone = pctTone(slaPct, true)
  const backPath = MissionId
    ? `/mission-details/${MissionId}`
    : '/missions'

  const pdfKpis = [
    {
      label: t('analysis.missionAnalysis.criticalIncidents'),
      desc: t('analysis.missionAnalysis.criticalIncidentsDesc'),
      value: `${formatDecimal(criticalIncidentsPct)} %`,
      progress: criticalIncidentsPct,
      tone: criticalTone,
    },
    {
      label: t('analysis.missionAnalysis.incidentsSlaUnder24h'),
      desc: t('analysis.missionAnalysis.slaDesc'),
      value: `${formatDecimal(slaPct)} %`,
      progress: slaPct,
      tone: slaTone,
    },
  ]

  const pdfProjectCharts = [
    {
      key: 'rate',
      kind: 'pie' as const,
      title: t('analysis.missionAnalysis.projectRate'),
      data: projectRateData,
    },
    {
      key: 'closure',
      kind: 'pie' as const,
      title: t('analysis.missionAnalysis.closureRateCriticalProjects'),
      data: criticalCompletedData,
    },
    {
      key: 'priority',
      kind: 'bar' as const,
      title: t('analysis.missionAnalysis.projectsByPriority'),
      data: ProjectPriorityData,
    },
    {
      key: 'type',
      kind: 'bar' as const,
      title: t('analysis.missionAnalysis.projectStatusByType'),
      data: taskTypesData,
    },
    {
      key: 'criticality',
      kind: 'bar' as const,
      title: t('analysis.missionAnalysis.criticalityVsStatus'),
      data: criticalityData,
    },
    {
      key: 'vuln',
      kind: 'bar' as const,
      title: t('analysis.missionAnalysis.vulnerabilityDistribution'),
      data: VulnerabilityDistributionData,
    },
  ]

  const pdfBudgetCharts = [
    {
      key: 'criticalBudget',
      kind: 'pie' as const,
      title: t('analysis.missionAnalysis.criticalBudgetPercentage'),
      data: delayedProjectsData,
    },
    {
      key: 'budgetType',
      kind: 'pie' as const,
      title: t('analysis.missionAnalysis.budgetByProjectType'),
      data: BudgetTypeProjectsData,
    },
    {
      key: 'incidentType',
      kind: 'pie' as const,
      title: t('analysis.missionAnalysis.incidentsByType'),
      data: incidentsByTypeData,
    },
    {
      key: 'perimeter',
      kind: 'pie' as const,
      title: t('analysis.missionAnalysis.incidentsByPerimeter'),
      data: incidentsByPerimeterData,
    },
    {
      key: 'overdue',
      kind: 'pie' as const,
      title: t('analysis.missionAnalysis.delayedProjects'),
      data: OverdueProjectData,
    },
  ]

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.missionAnalysis.eyebrow', {
          defaultValue: 'Operations · Missions',
        })}
        title={t('analysis.missionAnalysis.pageTitle', {
          defaultValue: 'Mission Analysis',
        })}
        subtitle={t('analysis.missionAnalysis.subtitle', {
          name: Mission?.MissionName || '—',
          date: computedOn,
          defaultValue: `Indicators for ${Mission?.MissionName || 'this mission'}, computed on ${computedOn}. Review project health, budget pressure and incident exposure.`,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.missionAnalysis.backToMission', {
                defaultValue: 'Back to mission',
              })}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(Mission?.MissionName || 'mission-analysis')
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={2} chartCount={4} />
      ) : (
        <>
      <div className="ma-kpi-grid">
        <div className="ma-kpi">
          <span
            className="ma-kpi-stamp"
            title={t('analysis.missionAnalysis.computedOn', {
              date: computedOn,
              defaultValue: `Computed on ${computedOn}`,
            })}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M8 2.5v5l3 1.5" />
              <circle cx="8" cy="8" r="5.5" />
            </svg>
          </span>
          <p className="ma-kpi-eyebrow">
            {t('analysis.missionAnalysis.criticalIncidents')}
          </p>
          <div className={`ma-kpi-value is-${criticalTone}`}>
            {formatDecimal(criticalIncidentsPct)}
            <span className="ma-kpi-suffix"> %</span>
          </div>
          <div className="ma-bar-track">
            <span
              className="ma-bar-fill"
              style={{
                width: `${criticalIncidentsPct}%`,
                background: barFill(criticalTone),
              }}
            />
          </div>
          <p className="ma-kpi-desc">
            {t('analysis.missionAnalysis.criticalIncidentsDesc', {
              defaultValue:
                'Share of incidents rated critical or high for this mission.',
            })}
          </p>
        </div>

        <div className="ma-kpi">
          <span
            className="ma-kpi-stamp"
            title={t('analysis.missionAnalysis.computedOn', {
              date: computedOn,
              defaultValue: `Computed on ${computedOn}`,
            })}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
              <path d="M2.5 6.5h11M5.5 2.5v2M10.5 2.5v2" />
            </svg>
          </span>
          <p className="ma-kpi-eyebrow">
            {t('analysis.missionAnalysis.incidentsSlaUnder24h')}
          </p>
          <div className={`ma-kpi-value is-${slaTone}`}>
            {formatDecimal(slaPct)}
            <span className="ma-kpi-suffix"> %</span>
          </div>
          <div className="ma-bar-track">
            <span
              className="ma-bar-fill"
              style={{
                width: `${slaPct}%`,
                background: barFill(slaTone),
              }}
            />
          </div>
          <p className="ma-kpi-desc">
            {t('analysis.missionAnalysis.slaDesc', {
              defaultValue:
                'Incidents handled within a 24-hour SLA window — higher is healthier.',
            })}
          </p>
        </div>
      </div>

      <AnalysisSection
        label={t('analysis.missionAnalysis.sectionProjects', {
          defaultValue: 'Projects',
        })}
      >
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.projectRate')}
            hasData={hasChartData(projectRateData)}
          >
            <PieChart
              embedded
              title={t('analysis.missionAnalysis.projectRate')}
              chartTitle={t('analysis.missionAnalysis.projectRate')}
              data={projectRateData}
              itemRow={2}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.closureRateCriticalProjects')}
            hasData={hasChartData(criticalCompletedData)}
          >
            <PieChart
              embedded
              title={t('analysis.missionAnalysis.closureRateCriticalProjects')}
              chartTitle={t(
                'analysis.missionAnalysis.closureRateCriticalProjects'
              )}
              data={criticalCompletedData}
              itemRow={2}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.projectsByPriority')}
            hasData={hasChartData(ProjectPriorityData)}
          >
            <BarChart
              embedded
              title={t('analysis.missionAnalysis.projectsByPriority')}
              chartTitle={t('analysis.missionAnalysis.projectsByPriority')}
              data={ProjectPriorityData}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.projectStatusByType')}
            hasData={hasChartData(taskTypesData)}
          >
            <BarChart
              embedded
              title={t('analysis.missionAnalysis.projectStatusByType')}
              chartTitle={t('analysis.missionAnalysis.projectStatusByType')}
              data={taskTypesData}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.criticalityVsStatus')}
            hasData={hasChartData(criticalityData)}
          >
            <BarChart
              embedded
              title={t('charts.priorityStatusDistribution.title')}
              chartTitle={t('analysis.missionAnalysis.criticalityVsStatus')}
              data={criticalityData}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.vulnerabilityDistribution')}
            hasData={hasChartData(VulnerabilityDistributionData)}
          >
            <BarChart
              embedded
              title={t('analysis.missionAnalysis.vulnerabilityDistribution')}
              chartTitle={t(
                'analysis.missionAnalysis.vulnerabilityDistribution'
              )}
              data={VulnerabilityDistributionData}
            />
          </AnalysisChartCard>
      </AnalysisSection>

      <AnalysisSection
        label={t('analysis.missionAnalysis.sectionBudgetIncidents', {
          defaultValue: 'Budget & incidents',
        })}
      >
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.criticalBudgetPercentage')}
            hasData={hasChartData(delayedProjectsData)}
          >
            <PieChart
              embedded
              title={t('analysis.missionAnalysis.criticalBudgetPercentage')}
              chartTitle={t('analysis.missionAnalysis.criticalBudgetPercentage')}
              data={delayedProjectsData}
              itemRow={2}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.budgetByProjectType')}
            hasData={hasChartData(BudgetTypeProjectsData)}
          >
            <PieChart
              embedded
              title={t('analysis.missionAnalysis.budgetByProjectType')}
              chartTitle={t('analysis.missionAnalysis.budgetByProjectType')}
              data={BudgetTypeProjectsData}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.incidentsByType')}
            hasData={hasChartData(incidentsByTypeData)}
          >
            <PieChart
              embedded
              title={t('analysis.missionAnalysis.incidentsByType')}
              chartTitle={t('analysis.missionAnalysis.incidentsByType')}
              data={incidentsByTypeData}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.incidentsByPerimeter')}
            hasData={hasChartData(incidentsByPerimeterData)}
          >
            <PieChart
              embedded
              title={t('analysis.missionAnalysis.incidentsByPerimeter')}
              chartTitle={t('analysis.missionAnalysis.incidentsByPerimeter')}
              data={incidentsByPerimeterData}
            />
          </AnalysisChartCard>
        <AnalysisChartCard
            title={t('analysis.missionAnalysis.delayedProjects')}
            hasData={hasChartData(OverdueProjectData)}
          >
            <PieChart
              embedded
              title={t('analysis.missionAnalysis.delayedProjects')}
              chartTitle={t('analysis.missionAnalysis.delayedProjects')}
              data={OverdueProjectData}
              itemRow={2}
            />
          </AnalysisChartCard>
      </AnalysisSection>

      {data.length > 0 ? (
        <>
          <p className="ma-section-label">
            {t('analysis.missionAnalysis.sectionBudgetTable', {
              defaultValue: 'Budget detail',
            })}
          </p>
          <div className="ma-card">
            <div className="ma-card-head">
              <h3>{t('analysis.missionAnalysis.budgetTableTitle')}</h3>
              <span className="ma-card-hint">
                {t('analysis.missionAnalysis.budgetTableHint', {
                  count: data.length,
                  defaultValue: `${data.length} projects`,
                })}
              </span>
            </div>
            <div className="ma-card-body ma-card-body--table">
              <MatrixComponent
                variant="analysis"
                title={t('analysis.missionAnalysis.budgetTableTitle')}
                data={data}
                headers={headers}
              />
            </div>
          </div>
        </>
      ) : null}
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('analysis.missionAnalysis.eyebrow')}
          title={Mission?.MissionName || '—'}
          subtitle={t('analysis.missionAnalysis.subtitle', {
            name: Mission?.MissionName || '—',
            date: computedOn,
          })}
          footerTitle={t('analysis.missionAnalysis.pageTitle')}
          computedOn={computedOn}
          kpis={pdfKpis}
          sections={[
            {
              label: t('analysis.missionAnalysis.sectionProjects'),
              charts: pdfProjectCharts,
            },
            {
              label: t('analysis.missionAnalysis.sectionBudgetIncidents'),
              charts: pdfBudgetCharts,
            },
          ]}
          tables={[
            {
              label: t('analysis.missionAnalysis.sectionBudgetTable'),
              title: t('analysis.missionAnalysis.budgetTableTitle'),
              hint: t('analysis.missionAnalysis.budgetTableHint', {
                count: data.length,
              }),
              headers,
              rows: data.map((row) => headers.map((h) => String(row[h] ?? ''))),
              numericLast: true,
            },
          ]}
        />
      </div>
    </div>
  )
}

export default MissionIndicatorPage
